// @ts-nocheck — ported from app/web (noUncheckedIndexedAccess)
/**
 * Async iterator over a streaming-preview HTTP link.
 *
 * Pull pipeline: consumer asks for the next event → reader.read() →
 * frame split → decode. A slow consumer stops reading, TCP fills, the
 * server's bounded frames channel fills, and the engine loop pauses —
 * backpressure without any buffering policy.
 *
 * Teardown: AbortController (normal path: unmount, supersede) rejects the
 * pending read with AbortError and the generator finishes silently via
 * its finally block (reader.cancel()). The server sees the connection
 * reset and cancels its engine loop.
 */

import { BarDecodeState, decodeFrame, type StreamEvent } from './previewStreamDecoder';

export class StreamGoneError extends Error {
	constructor(public status: number) {
		super(`stream link invalid (HTTP ${status})`);
		this.name = 'StreamGoneError';
	}
}

export interface StreamIterator {
	[Symbol.asyncIterator](): AsyncIterator<StreamEvent>;
}

/**
 * Inflates the compressed-frame fragments of the historical section: one
 * continuous deflate-raw stream, each fragment sync-flushed by the server,
 * so after writing a fragment exactly uncompressedLen bytes are readable
 * — deterministic, no holdback. Native DecompressionStream (all modern
 * browsers + Node 18+). Not used after the historicalDone event.
 *
 * Chrome quirk (the reason writes are never awaited before reads):
 * DecompressionStream's writer.write() promise resolves only when the
 * readable side is pulled — write completion is coupled to downstream
 * consumption (backpressure). Awaiting write() before reading deadlocks
 * Chrome (Node resolves eagerly, hiding the bug). So inflate() issues the
 * write, reads the expected bytes out concurrently, and only then awaits
 * the write promise.
 */
export class BlockInflater {
	private writer: WritableStreamDefaultWriter<BufferSource>;
	private reader: ReadableStreamDefaultReader<Uint8Array>;

	constructor() {
		const { writable, readable } = new DecompressionStream('deflate-raw');
		this.writer = writable.getWriter() as WritableStreamDefaultWriter<BufferSource>;
		this.reader = readable.getReader();
	}

	/** Decode one fragment. Resolves when all its bytes are out. */
	async inflate(fragment: Uint8Array, uncompressedLen: number): Promise<Uint8Array> {
		const writePromise = this.writer.write(new Uint8Array(fragment));
		if (uncompressedLen === 0) {
			await writePromise;
			return new Uint8Array(0);
		}
		const out = new Uint8Array(uncompressedLen);
		let got = 0;
		while (got < uncompressedLen) {
			const { done, value } = await this.reader.read();
			if (done) throw new Error('compressed stream ended early');
			out.set(value, got);
			got += value.length;
		}
		await writePromise;
		return out;
	}

	/** Call when the historical section ends (historicalDone, or stream
	 * end without it): releases the decompressor. */
	end(): void {
		this.writer.close().catch(() => {});
	}
}

/**
 * Split incoming chunks into frames, reusing one buffer. A payload view is
 * valid only during the corresponding event's synchronous handling: the
 * generator never writes to the buffer while the consumer holds an event
 * (it is paused at yield), so views die within one iteration.
 * Exported for direct (test) use.
 */
export class FrameSplitter {
	private buf: Uint8Array;
	private len = 0; // bytes buffered
	private pos = 0; // parse cursor

	constructor(initialCap = 64 * 1024) {
		this.buf = new Uint8Array(initialCap);
	}

	/** Append a chunk, compacting/growing as needed. */
	push(chunk: Uint8Array): void {
		// Compact consumed bytes to the front.
		if (this.pos > 0) {
			this.len -= this.pos;
			this.buf.copyWithin(0, this.pos, this.len + this.pos);
			this.pos = 0;
		}
		// Grow if the chunk doesn't fit.
		if (this.len + chunk.length > this.buf.length) {
			let cap = this.buf.length;
			while (cap < this.len + chunk.length) cap *= 2;
			const nb = new Uint8Array(cap);
			nb.set(this.buf.subarray(0, this.len));
			this.buf = nb;
		}
		this.buf.set(chunk, this.len);
		this.len += chunk.length;
	}

	/** Next complete frame, or null if more bytes are needed. */
	next(): { type: number; payload: Uint8Array } | null {
		const avail = this.len - this.pos;
		if (avail < 3) return null;
		const type = this.buf[this.pos];
		const plen = this.buf[this.pos + 1] | (this.buf[this.pos + 2] << 8);
		if (avail < 3 + plen) return null;
		const payload = this.buf.subarray(this.pos + 3, this.pos + 3 + plen);
		this.pos += 3 + plen;
		return { type, payload };
	}
}

/**
 * Consume a streaming-preview link as an async iterator of typed events.
 * The iterator finishes when the server sends its done frame and closes,
 * or when the stream ends without one (premature close — the consumer
 * should treat accumulated data as stale). Aborts finish silently.
 */
export async function* streamEvents(url: string, signal: AbortSignal): AsyncGenerator<StreamEvent> {
	let response: Response;
	try {
		response = await fetch(url, { signal, cache: 'no-store' });
	} catch (err) {
		if ((err as Error).name === 'AbortError') return;
		throw err;
	}
	// fetch does not reject on HTTP error status — unknown/expired links
	// surface here.
	if (!response.ok || !response.body) {
		throw new StreamGoneError(response.status);
	}

	const reader = response.body.getReader();
	try {
		const splitter = new FrameSplitter();
		const inner = new FrameSplitter(); // frames inside compressed fragments
		const inflater = new BlockInflater();
		let plotCount = 0;
		let barState: BarDecodeState | null = null;
		let live = false; // after historicalDone: no more compressed frames
		let pending: { type: number; payload: Uint8Array } | null = null;
		// Yield to the event loop (macrotask) every N events. A pure
		// read→decode→yield chain runs as an unbroken microtask sequence
		// when the replay is faster than consumption: the browser gets no
		// event-loop turn, painting and timers starve, and the main thread
		// appears blocked for the whole historical burst. One real yield
		// every 512 events keeps the page responsive at negligible cost
		// (~1 scheduler yield per few thousand bars).
		let eventsSinceYield = 0;
		const yieldToEventLoop = () =>
			new Promise<void>((resolve) => {
				const sched = (globalThis as any).scheduler;
				if (typeof sched?.postTask === 'function') {
					sched.postTask(resolve, { priority: 'user-visible' });
				} else {
					setTimeout(resolve, 0);
				}
			});

		const handle = (ev: StreamEvent) => {
			if (ev.t === 'meta') {
				plotCount = ev.meta.plots.length;
				if (ev.meta.priceTick > 0) barState = new BarDecodeState(ev.meta.priceTick);
			}
			if (ev.t === 'historicalDone') {
				live = true;
				inflater.end(); // the deflate stream ended before this frame
			}
		};

		for (;;) {
			// Drain any frames already buffered from the previous chunk
			// before reading more (a chunk can carry many frames).
			for (;;) {
				pending = splitter.next();
				if (!pending) break;
				if (pending.type === 0x0b /* FRAME_COMPRESSED */ && !live) {
					// [u32 uncompressed_len][deflate fragment]
					const pl = pending.payload;
					const ulen = pl[0] | (pl[1] << 8) | (pl[2] << 16) | (pl[3] << 24);
					const fragment = pl.subarray(4);
					inner.push(await inflater.inflate(fragment, ulen));
					// Emit the frames inside the fragment in order.
					for (;;) {
						const f = inner.next();
						if (!f) break;
						const ev = decodeFrame(f.type, f.payload, plotCount, barState);
						if (!ev) continue; // unknown frame type: skip
						handle(ev);
						if (++eventsSinceYield >= 512) {
							eventsSinceYield = 0;
							await yieldToEventLoop();
						}
						yield ev;
					}
					continue;
				}
				const ev = decodeFrame(pending.type, pending.payload, plotCount, barState);
				if (!ev) continue; // unknown frame type: skip
				handle(ev);
				if (++eventsSinceYield >= 512) {
					eventsSinceYield = 0;
					await yieldToEventLoop();
				}
				yield ev;
			}
			const { done, value } = await reader.read();
			if (done) {
				// Premature close without done frame = stale. The inflater
				// may still be open when the server died mid-history.
				inflater.end();
				return;
			}
			splitter.push(value);
		}
	} finally {
		// Covers abort (read rejects), consumer break (return()), and
		// thrown decode errors.
		reader.cancel().catch(() => {});
	}
}
